<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useQuasar } from 'quasar';

import ReorderableTable from '@/components/ReorderableTable.vue';
import CreateFormDialog from './dialogs/CreateFormDialog.vue';
import RelocateElementDialog from './dialogs/RelocateElementDialog.vue';

import type { ConsultationDto, FormDto, Locales, MoveDirection, MoveState } from '@/services/types/api.types';
import { useFormsStore, useSectionsStore } from '@/stores';
import { Constants } from '@/constants';
import { getTranslation } from '@/composables/data-display-utils';
import {
  activeRuleSets,
  describeConflicts,
  formTitle,
  relocationConflicts,
  swapConflicts,
} from '@/helpers/visibilityRules.helper';
import { confirmDelete } from '@/helpers/dialogs.helper';

const router = useRouter();
const { t } = useI18n();
const $q = useQuasar();

// STORE
const formsStore = useFormsStore();
const sectionsStore = useSectionsStore();

// PROPS
const props = withDefaults(
  defineProps<{
    sectionId: number;
    consultationId: number;
    consultation?: ConsultationDto | null;
    languageEnabled: Locales[];
    isTemplate?: boolean;
  }>(),
  {
    isTemplate: false,
    consultation: null,
  },
);

// LIFECYCLE
onMounted(async () => {
  await formsStore.fetchFormsBySection(props.sectionId);
  await sectionsStore.fetchSectionsByConsultation(props.consultationId);
});

// COMPUTED
const forms = computed(() => formsStore.getFormsBySectionId(props.sectionId).sort((a, b) => a.order_num - b.order_num));

const formActions = computed(() => [
  {
    action: 'relocate-form',
    label: t('section.dialogs.move.title'),
    disabled: sectionsStore.getSectionsByConsultationId(props.consultationId).length === 1,
  },
]);

const isGuidedConsultation = computed(
  () => props.consultation?.participation_mode === Constants.Consultation.ParticipationMode.Guided,
);

const visibilityTargetIds = computed(() => {
  const ruleSets = props.consultation?.options?.guided_consultation?.visibility?.ruleSets ?? [];
  return new Set(ruleSets.map((rs) => rs.targetId));
});

const ruleSets = computed(() => activeRuleSets(props.consultation));
const tableForms = computed(() => forms.value.filter((form) => form.is_citizen_proposal === 0));

// A swap exchanges the two positions, so the questions in between, citizen proposals included, keep theirs. A move
// within a section cannot break a rule whose source or target is in another one.
const getMoveState = (row: FormDto, direction: MoveDirection): MoveState => {
  const neighbour =
    tableForms.value[tableForms.value.findIndex(({ id }) => id === row.id) + (direction === 'up' ? -1 : 1)];
  if (!neighbour) return { isBlocked: false };
  const conflicts = swapConflicts(
    ruleSets.value,
    forms.value.map((form) => [form]),
    forms.value.findIndex(({ id }) => id === row.id),
    forms.value.findIndex(({ id }) => id === neighbour.id),
  );
  return conflicts.length > 0
    ? {
        isBlocked: true,
        reason: describeConflicts(conflicts, (id) => formTitle(forms.value, id, props.languageEnabled)),
      }
    : { isBlocked: false };
};

const columns = computed(() => {
  const cols = [
    {
      name: 'title',
      label: t('global.label.title'),
      align: 'left' as const,
      field: (row: FormDto) => getTranslation(row.question, 'title', props.languageEnabled),
    },
    {
      name: 'type',
      label: t('global.label.type'),
      align: 'left' as const,
      field: (row: FormDto) => t(`form.types.${row.type}`),
    },
  ];
  if (isGuidedConsultation.value) {
    cols.push({
      name: 'guided_consultation_badges',
      label: t('form.guided-consultation.title'),
      align: 'left' as const,
      field: () => '',
    });
  }
  return cols;
});

const citizenFormColumn = computed(() => [
  ...columns.value, // Spread the shared columns
  {
    name: 'is_citizen_proposal',
    label: t('global.label.citizenProposal'),
    align: 'center',
    field: (row: FormDto) => (row.is_citizen_proposal ? t(`form.is_citizen_proposal`) : ''),
  },
  {
    name: 'approbation',
    label: t('global.label.approbationStatus'),
    align: 'left',
    field: (row: FormDto) => {
      if (row.is_citizen_proposal) {
        if (row.moderation_key !== null) {
          return t(`form.status.need_moderation`);
        } else if (row.status === Constants.Form.Status.Deleted) {
          return t(`form.status.options.refused`);
        } else {
          return t(`form.status.options.approved`);
        }
      }
    },
  },
]);

// FUNCTIONS
const handleFormRowClicked = (row: FormDto) => {
  if (props.isTemplate) {
    router.push({
      name: 'form_template_details',
      params: { consultationId: props.consultationId, sectionId: props.sectionId, id: row.id },
    });
  } else {
    router.push({ name: 'form_details', params: { id: row.id } });
  }
};

const handleAddRow = () => {
  const form = formsStore.createForm(props.sectionId);
  $q.dialog({
    component: CreateFormDialog,
    componentProps: {
      consultationId: props.consultationId,
      sectionId: props.sectionId,
      form,
      languageEnabled: props.languageEnabled,
      isTemplate: props.isTemplate,
    },
  });
};

const handleDeleteRow = (row: FormDto) => {
  confirmDelete(t('form.name')).onOk(() => {
    formsStore.deleteForm(row);
  });
};

const handleReorder = (source: FormDto, target: FormDto) => {
  // TODO: this should be atomic
  formsStore.saveForm(source);
  formsStore.saveForm(target);
};

const handleActions = async (action: any) => {
  if (action.action === 'relocate-form') {
    const sections = [...sectionsStore.getSectionsByConsultationId(props.consultationId)].sort(
      (a, b) => a.order_num - b.order_num,
    );
    // The question goes to the end of the section it is moved to: the sections that would put it after a question
    // whose display depends on its answer are offered grayed out.
    if (ruleSets.value.length > 0) {
      await Promise.all(sections.map((section) => formsStore.fetchFormsBySection(section.id!)));
    }
    const sectionForms = sections.map((section) =>
      formsStore.getFormsBySectionId(section.id!).sort((a, b) => a.order_num - b.order_num),
    );
    const allForms = sectionForms.flat();
    const filteredSections = sections.flatMap((section, index) => {
      if (section.id === props.sectionId) return [];
      const label = getTranslation(section, 'title', props.languageEnabled);
      const conflicts = relocationConflicts(ruleSets.value, sectionForms, action.payload.row, index);
      if (conflicts.length === 0) return [{ value: section.id, label }];
      const reason = describeConflicts(
        conflicts,
        (id) => formTitle(allForms, id, props.languageEnabled),
        'form.guided-consultation.relocateBlocked',
        { section: label },
      );
      return [{ value: section.id, label: reason, disable: true }];
    });

    $q.dialog({
      component: RelocateElementDialog,
      componentProps: {
        title: t('section.dialogs.move.title'),
        filterElementLabel: t('section.dialogs.move.label'),
        options: filteredSections,
        relocatedElement: action.payload,
        name: 'section',
        targetName: 'section_id',
        languageEnabled: props.languageEnabled,
      },
    });
  }
};
</script>

<template>
  <div>
    <reorderable-table
      :rows="tableForms"
      :columns="columns"
      :title="t('form.list')"
      itemKey="id"
      :action-menu-items="formActions"
      :get-move-state="getMoveState"
      @rowClick="handleFormRowClicked"
      @reorder="handleReorder"
      @add-row="handleAddRow"
      @delete-row="handleDeleteRow"
      @actions="handleActions"
    >
      <template v-if="isGuidedConsultation" v-slot:body-cell-guided_consultation_badges="slotProps">
        <q-td :props="slotProps">
          <div class="row q-gutter-xs">
            <q-badge v-if="slotProps.row.is_mandatory === 1" color="primary" outline>
              {{ t('form.guided-consultation.summary.mandatory') }}
            </q-badge>
            <q-badge v-if="visibilityTargetIds.has(slotProps.row.id)" color="orange" outline>
              {{ t('form.guided-consultation.summary.conditional') }}
            </q-badge>
          </div>
        </q-td>
      </template>
    </reorderable-table>
    <reorderable-table
      class="q-mt-xl"
      :rows="forms.filter((f) => f.is_citizen_proposal === 1)"
      :columns="citizenFormColumn"
      :title="t('form.citizenList')"
      itemKey="id"
      :disable-orderable="true"
      :disable-add-button="true"
      @rowClick="handleFormRowClicked"
      @reorder="handleReorder"
      @add-row="handleAddRow"
      @delete-row="handleDeleteRow"
    >
      <template v-slot:body-cell-is_citizen_proposal="props">
        <q-td :props="props">
          <q-badge v-if="props.row.is_citizen_proposal">
            <q-icon name="check" />
          </q-badge>
        </q-td>
      </template>
    </reorderable-table>
  </div>
</template>
